import React, { useEffect, useState, useRef, useImperativeHandle, forwardRef } from 'react';
import { Button, Input, Table } from 'reactstrap';
import * as am4core from '@amcharts/amcharts4/core';
import * as am4maps from '@amcharts/amcharts4/maps';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import cities from './mock';
import roteNdaoGeo from '../../assets/jsonData/kab_Rote.json';
import TulufaliLogo from '../Icons/tulufali_logo.svg';
import s from './am4chartMap.module.scss';
import './tooltip.css';
import { GET_REKAP_DTSEN_DESA } from '../../utils';
import { numberWithCommas } from '../../utils/func';
import axios from 'axios';
import { API_TIMEOUT } from '../../utils';
import {
  loadMapSettings,
  MAP_SETTINGS_CHANGED_EVENT,
  DEFAULT_MAP_SETTINGS,
  normalizeMapSettings,
} from '../../utils/mapSettings';
import {
  normalizeForMatch,
  extendBboxFromGeometry,
  resolveKecamatanIncludedFilter,
} from '../../utils/mapGeoMerge';
import { loadMapCsvRows, MAP_CSV_CHANGED_EVENT } from '../../utils/mapCsvData';
import {
  quantileBreaks,
  valueToTierIndex,
  ensureTierPalette,
  legendLabelsForTiers,
  ensureManualBreaks,
} from '../../utils/mapTierUtils';

const TAGLINE = 'Tata Kelola Bantuan Terintegrasi Untuk Penanggulangan Kemiskinan Rote Ndao';

function computeKecamatanLabelPoints(geoFeatures, includedKecSet) {
  const byKec = new Map();
  geoFeatures.forEach((f) => {
    const displayName = f.properties?.KECAMATAN;
    const key = normalizeForMatch(displayName);
    if (!key) return;
    if (includedKecSet && !includedKecSet.has(key)) return;
    const cen = centroidFromGeometry(f.geometry);
    if (!cen) return;
    if (!byKec.has(key)) {
      byKec.set(key, { displayName, lngSum: 0, latSum: 0, n: 0 });
    }
    const o = byKec.get(key);
    o.lngSum += cen[0];
    o.latSum += cen[1];
    o.n += 1;
  });
  return [...byKec.values()].map((o) => ({
    latitude: o.latSum / o.n,
    longitude: o.lngSum / o.n,
    title: o.displayName,
  }));
}

function ringCentroid(ring) {
  if (!ring || ring.length < 3) return null;
  let sx = 0;
  let sy = 0;
  const n = ring.length;
  for (let i = 0; i < n; i++) {
    sx += ring[i][0];
    sy += ring[i][1];
  }
  return [sx / n, sy / n];
}

function centroidFromGeometry(geom) {
  if (!geom) return null;
  if (geom.type === 'Polygon') {
    return ringCentroid(geom.coordinates[0]);
  }
  if (geom.type === 'MultiPolygon') {
    let sx = 0;
    let sy = 0;
    let c = 0;
    for (let p = 0; p < geom.coordinates.length; p++) {
      const cen = ringCentroid(geom.coordinates[p][0]);
      if (cen) {
        sx += cen[0];
        sy += cen[1];
        c++;
      }
    }
    return c ? [sx / c, sy / c] : null;
  }
  return null;
}

const DESIL_OPTIONS = [
  { value: 1, label: 'Desil 1 (Sangat Miskin)' },
  { value: 2, label: 'Desil 2 (Miskin)' },
  { value: 3, label: 'Desil 3 (Rentan Miskin)' },
  { value: 4, label: 'Desil 4 (Hampir Rentan)' },
  { value: 5, label: 'Desil 5 (Menengah Bawah)' },
  { value: 6, label: 'Desil 6–10 (Menengah Atas)' },
  { value: 7, label: 'Persen Desil 1 (thd total penduduk)' },
  { value: 8, label: 'Persen Desil 1+2 (thd total penduduk)' },
];

const Am4chartMapDesil = forwardRef(function Am4chartMapDesil(
  {
    previewSettings,
    previewMode = false,
    onManualColorRequest,
    /** Halaman pengaturan peta: tanpa overlay judul, peta lebih tinggi */
    customizationEmbed = false,
  },
  ref
) {
  const onManualCbRef = useRef(onManualColorRequest);
  onManualCbRef.current = onManualColorRequest;

  const [storedMapSettings, setStoredMapSettings] = useState(() => loadMapSettings());
  const [listDesilDesa, setListDesilDesa] = useState([]);
  const [selectedDesil, setSelectedDesil] = useState(1);
  const [isLoadingMap, setIsLoadingMap] = useState(false);
  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);
  const am4ChartRef = useRef(null);
  const mapWrapRef = useRef(null);
  const [mapCombine, setMapCombine] = useState();
  const [legendMeta, setLegendMeta] = useState({
    breaks: [],
    tierCount: 4,
    labels: ['—', '—', '—', '—'],
    isPercentMode: false,
    tierPalette: [],
    isCsv: true,
  });
  const [csvTick, setCsvTick] = useState(0);

  const usePreview = previewSettings !== undefined;
  const mapUiSettings = usePreview ? normalizeMapSettings(previewSettings) : storedMapSettings;
  const desilColors = mapUiSettings.colors || DEFAULT_MAP_SETTINGS.colors;
  const mapDomId = previewMode ? 'mapDesilPreview' : 'mapDesil';
  const dataSource = mapUiSettings.dataSource === 'api' ? 'api' : 'csv';

  const mapStyleFingerprint = JSON.stringify({
    colors: desilColors,
    showKecamatanLabels: !!mapUiSettings.showKecamatanLabels,
    mapLevel: mapUiSettings.mapLevel,
    dataSource: mapUiSettings.dataSource,
    tierCount: mapUiSettings.tierCount,
    tierMode: mapUiSettings.tierMode,
    manualTierBreaks: mapUiSettings.manualTierBreaks,
    tierPalette: mapUiSettings.tierPalette,
    polygonManualColors: mapUiSettings.polygonManualColors,
    kecamatanIncludedKeys: mapUiSettings.kecamatanIncludedKeys,
  });

  useEffect(() => {
    if (usePreview) return undefined;
    const onChange = () => setStoredMapSettings(loadMapSettings());
    window.addEventListener(MAP_SETTINGS_CHANGED_EVENT, onChange);
    window.addEventListener('storage', onChange);
    return () => {
      window.removeEventListener(MAP_SETTINGS_CHANGED_EVENT, onChange);
      window.removeEventListener('storage', onChange);
    };
  }, [usePreview]);

  useEffect(() => {
    const onCsv = () => setCsvTick((t) => t + 1);
    window.addEventListener(MAP_CSV_CHANGED_EVENT, onCsv);
    window.addEventListener('storage', onCsv);
    return () => {
      window.removeEventListener(MAP_CSV_CHANGED_EVENT, onCsv);
      window.removeEventListener('storage', onCsv);
    };
  }, []);

  const getListDesilDesa = async () => {
    setIsLoadingMap(true);
    try {
      const res = await axios.get(GET_REKAP_DTSEN_DESA, { timeout: API_TIMEOUT });
      const data = res.data?.data || [];
      setListDesilDesa(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('getListDesilDesa:', err);
      setListDesilDesa([]);
    } finally {
      setIsLoadingMap(false);
    }
  };

  useEffect(() => {
    if (dataSource === 'api') {
      getListDesilDesa();
    } else {
      setIsLoadingMap(false);
      setListDesilDesa(loadMapCsvRows());
    }
  }, [dataSource, csvTick]);

  useEffect(() => {
    const mapLevel = mapUiSettings.mapLevel === 'kecamatan' ? 'kecamatan' : 'desa';
    const tierCount = Math.max(2, Math.min(8, Number(mapUiSettings.tierCount) || 4));
    const tierMode = mapUiSettings.tierMode === 'manual' ? 'manual' : 'quantile';
    const isPercentMode = dataSource === 'api' && (selectedDesil === 7 || selectedDesil === 8);
    const desilKey = selectedDesil === 6 ? 'Desil_6_10' : selectedDesil <= 6 ? `Desil_${selectedDesil}` : null;

    const tierPalette = ensureTierPalette(mapUiSettings.tierPalette, tierCount, [
      mapUiSettings.colors?.low,
      mapUiSettings.colors?.medium,
      mapUiSettings.colors?.high,
      mapUiSettings.colors?.highest,
    ]);
    const polygonManualColors = mapUiSettings.polygonManualColors || {};
    const colorsNone = mapUiSettings.colors?.none || '#e8e8e8';

    const desaLookup = new Map();
    listDesilDesa.forEach((item) => {
      const key = normalizeForMatch(item?.desa);
      if (key) desaLookup.set(key, item);
    });

    const getValueForApiDesa = (row) => {
      if (!row) return 0;
      if (isPercentMode) {
        const total = Number(row.jumlahOrang) || 0;
        if (total <= 0) return 0;
        const num =
          selectedDesil === 7
            ? Number(row.Desil_1) || 0
            : (Number(row.Desil_1) || 0) + (Number(row.Desil_2) || 0);
        return (num / total) * 100;
      }
      return Number(row[desilKey]) || 0;
    };

    const getMetricDesaFromRow = (row) => {
      if (dataSource === 'csv') return Number(row?.nilai) || 0;
      return getValueForApiDesa(row);
    };

    const kecMetric = new Map();
    listDesilDesa.forEach((row) => {
      const k = normalizeForMatch(row?.kecamatan);
      if (!k) return;
      kecMetric.set(k, (kecMetric.get(k) || 0) + getMetricDesaFromRow(row));
    });

    /**
     * Mode kecamatan: tetap pakai polygon per desa (semua wilayah tetap tampil),
     * warna diambil dari agregat per kecamatan sehingga satu kecamatan = satu warna.
     * Menggabung MultiPolygon besar sering membuat amCharts hanya merender sebagian.
     */
    const rawFeatures = roteNdaoGeo.features;

    const allKecKeysFromGeo = new Set();
    for (let ri = 0; ri < rawFeatures.length; ri++) {
      const kk = normalizeForMatch(rawFeatures[ri].properties?.KECAMATAN);
      if (kk) allKecKeysFromGeo.add(kk);
    }
    const kecIncludedFilter = resolveKecamatanIncludedFilter(
      mapLevel,
      mapUiSettings.kecamatanIncludedKeys,
      allKecKeysFromGeo
    );

    const displayValues = [];
    if (mapLevel === 'kecamatan') {
      kecMetric.forEach((v, k) => {
        if (kecIncludedFilter.active && kecIncludedFilter.set && !kecIncludedFilter.set.has(k)) {
          return;
        }
        if (v > 0) displayValues.push(v);
      });
    } else {
      for (let i = 0; i < rawFeatures.length; i++) {
        const f = rawFeatures[i];
        const dk = normalizeForMatch(f.properties?.DESA);
        const row = desaLookup.get(dk);
        const v = getMetricDesaFromRow(row);
        if (v > 0) displayValues.push(v);
      }
    }
    displayValues.sort((a, b) => a - b);

    let breaks =
      tierMode === 'manual'
        ? ensureManualBreaks(mapUiSettings.manualTierBreaks, tierCount)
        : quantileBreaks(displayValues, tierCount);

    if (tierMode === 'quantile' && displayValues.length === 0) {
      breaks = ensureManualBreaks(mapUiSettings.manualTierBreaks, tierCount);
    }

    const labels = legendLabelsForTiers(breaks, tierCount, isPercentMode);
    setLegendMeta({ breaks, tierCount, labels, isPercentMode, tierPalette, isCsv: dataSource === 'csv' });

    const getFillColor = (desaKeyNorm, kecKeyNorm) => {
      if (
        mapLevel === 'kecamatan' &&
        kecIncludedFilter.active &&
        kecIncludedFilter.set &&
        kecKeyNorm &&
        !kecIncludedFilter.set.has(kecKeyNorm)
      ) {
        return colorsNone;
      }
      const manualKey = mapLevel === 'desa' ? `desa:${desaKeyNorm}` : `kec:${kecKeyNorm}`;
      if (polygonManualColors[manualKey]) return polygonManualColors[manualKey];
      let val = 0;
      if (mapLevel === 'desa') {
        const row = desaKeyNorm ? desaLookup.get(desaKeyNorm) : null;
        val = getMetricDesaFromRow(row);
      } else {
        val = kecMetric.get(kecKeyNorm) || 0;
      }
      const ix = valueToTierIndex(val, breaks, tierCount);
      if (ix < 0) return colorsNone;
      return tierPalette[ix] || colorsNone;
    };

    const desaku = [];
    for (let i = 0; i < rawFeatures.length; i++) {
      const desa = JSON.parse(JSON.stringify(rawFeatures[i]));
      const lookupKey = normalizeForMatch(desa.properties?.DESA);
      const kecKey = normalizeForMatch(desa.properties?.KECAMATAN);
      desa.properties._kecKeyNorm = kecKey;
      const row = mapLevel === 'desa' ? desaLookup.get(lookupKey) : null;

      const kecDimmed =
        mapLevel === 'kecamatan' &&
        kecIncludedFilter.active &&
        kecIncludedFilter.set &&
        kecKey &&
        !kecIncludedFilter.set.has(kecKey);

      let count = 0;
      if (mapLevel === 'desa') {
        count = getMetricDesaFromRow(row);
      } else if (kecDimmed) {
        count = 0;
      } else {
        count = kecKey ? kecMetric.get(kecKey) || 0 : 0;
      }

      desa.properties.mapDisplayValue = count;
      desa.properties._manualKey = mapLevel === 'desa' ? `desa:${lookupKey}` : `kec:${kecKey}`;
      desa.properties._displayLabel =
        mapLevel === 'desa' ? desa.properties?.DESA || '' : desa.properties?.KECAMATAN || '';

      if (dataSource === 'api' && mapLevel === 'desa') {
        desa.properties.penerimaDesil = isPercentMode ? Number(count.toFixed(1)) : count;
        if (isPercentMode) {
          if (row) {
            const totKK = Number(row.jumlahKK) || 0;
            desa.properties.penerimaDesilKK =
              totKK > 0
                ? (
                    selectedDesil === 7
                      ? ((Number(row.Desil_1_KK) || 0) / totKK) * 100
                      : (((Number(row.Desil_1_KK) || 0) + (Number(row.Desil_2_KK) || 0)) / totKK) * 100
                  ).toFixed(1)
                : '0';
          } else {
            desa.properties.penerimaDesilKK = '0';
          }
        } else {
          desa.properties.penerimaDesilKK = '0';
        }
        desa.properties.jumlahOrang = row ? Number(row.jumlahOrang) || 0 : 0;
        desa.properties.jumlahKK = row ? Number(row.jumlahKK) || 0 : 0;
        desa.properties.desil1 = row ? Number(row.Desil_1) || 0 : 0;
        desa.properties.desil2 = row ? Number(row.Desil_2) || 0 : 0;
        desa.properties.desil3 = row ? Number(row.Desil_3) || 0 : 0;
        desa.properties.desil4 = row ? Number(row.Desil_4) || 0 : 0;
        desa.properties.desil5 = row ? Number(row.Desil_5) || 0 : 0;
        desa.properties.desil610 = row ? Number(row.Desil_6_10) || 0 : 0;
        desa.properties.desil1KK = row ? Number(row.Desil_1_KK) || 0 : 0;
        desa.properties.desil2KK = row ? Number(row.Desil_2_KK) || 0 : 0;
        desa.properties.desil3KK = row ? Number(row.Desil_3_KK) || 0 : 0;
        desa.properties.desil4KK = row ? Number(row.Desil_4_KK) || 0 : 0;
        desa.properties.desil5KK = row ? Number(row.Desil_5_KK) || 0 : 0;
        desa.properties.desil610KK = row ? Number(row.Desil_6_10_KK) || 0 : 0;
        const totOrang = desa.properties.jumlahOrang;
        const totKK = desa.properties.jumlahKK;
        const fmt = (n, d) => (d > 0 ? ((n / d) * 100).toFixed(1) : '0');
        desa.properties.pctDesil1 = fmt(desa.properties.desil1, totOrang);
        desa.properties.pctDesil2 = fmt(desa.properties.desil2, totOrang);
        desa.properties.pctDesil3 = fmt(desa.properties.desil3, totOrang);
        desa.properties.pctDesil4 = fmt(desa.properties.desil4, totOrang);
        desa.properties.pctDesil5 = fmt(desa.properties.desil5, totOrang);
        desa.properties.pctDesil610 = fmt(desa.properties.desil610, totOrang);
        desa.properties.pctDesil1KK = fmt(desa.properties.desil1KK, totKK);
        desa.properties.pctDesil2KK = fmt(desa.properties.desil2KK, totKK);
        desa.properties.pctDesil3KK = fmt(desa.properties.desil3KK, totKK);
        desa.properties.pctDesil4KK = fmt(desa.properties.desil4KK, totKK);
        desa.properties.pctDesil5KK = fmt(desa.properties.desil5KK, totKK);
        desa.properties.pctDesil610KK = fmt(desa.properties.desil610KK, totKK);
      } else {
        desa.properties.penerimaDesil = count;
        desa.properties.penerimaDesilKK = '0';
        desa.properties.jumlahOrang = 0;
        desa.properties.jumlahKK = 0;
      }

      desa.properties.color = getFillColor(lookupKey, kecKey);
      desaku.push(desa);
    }

    setMapCombine({
      type: 'FeatureCollection',
      features: desaku,
    });
  }, [listDesilDesa, selectedDesil, mapStyleFingerprint, dataSource]);

  useEffect(() => {
    if (!mapCombine) return;

    if (am4ChartRef.current) {
      am4ChartRef.current.dispose();
      am4ChartRef.current = null;
    }

    const map = am4core.create(mapDomId, am4maps.MapChart);
    am4ChartRef.current = map;
    if (map.logo) map.logo.hide();
    setTimeout(() => {
      if (map.logo) map.logo.hide();
    }, 500);
    map.maxZoomLevel = 4;
    map.maxPanOut = 0.75;
    map.geodata = mapCombine;
    map.percentHeight = 90;
    map.dy = 10;

    const polygonSeries = map.series.push(new am4maps.MapPolygonSeries());
    polygonSeries.useGeodata = true;
    map.homeZoomLevel = 1;
    map.zoomControl = new am4maps.ZoomControl();
    map.zoomControl.layout = 'horizontal';
    map.zoomControl.align = 'left';
    map.zoomControl.valign = 'bottom';
    map.zoomControl.dy = 30;
    map.zoomControl.dx = 30;
    map.zoomControl.contentHeight = 20;
    map.zoomControl.minusButton.background.fill = am4core.color('#C7D0FF');
    map.zoomControl.minusButton.background.stroke = am4core.color('white');
    map.zoomControl.minusButton.label.fontWeight = 600;
    map.zoomControl.minusButton.label.fontSize = 22;
    map.zoomControl.minusButton.scale = 0.75;
    map.zoomControl.minusButton.label.scale = 0.75;
    map.zoomControl.plusButton.background.fill = am4core.color('#C7D0FF');
    map.zoomControl.plusButton.background.stroke = am4core.color('#6979C9');
    map.zoomControl.plusButton.label.fontWeight = 600;
    map.zoomControl.plusButton.label.fontSize = 22;
    map.zoomControl.plusButton.label.align = 'center';
    map.zoomControl.plusButton.scale = 0.75;
    map.zoomControl.plusButton.label.scale = 0.75;
    map.zoomControl.plusButton.dx = 5;
    const plusHover = map.zoomControl.plusButton.background.states.create('hover');
    plusHover.properties.fill = am4core.color('#354D84');
    const minusHover = map.zoomControl.minusButton.background.states.create('hover');
    minusHover.properties.fill = am4core.color('#354D84');

    const polygonTemplate = polygonSeries.mapPolygons.template;

    const isCsv = dataSource === 'csv';
    const mapLevel = mapUiSettings.mapLevel === 'kecamatan' ? 'kecamatan' : 'desa';

    if (isCsv || mapLevel === 'kecamatan') {
      polygonTemplate.tooltipHTML = `
      <div class="map-tooltip-card">
        <div class="map-tooltip-title"><strong>{DESA}</strong> <span class="map-tooltip-sub">· {KECAMATAN}</span></div>
        <div class="map-tooltip-metric"><span class="map-tooltip-label">Nilai</span><strong>{mapDisplayValue}</strong></div>
      </div>`;
    } else {
      const tooltipRows = {
        1: () =>
          `<div class="map-tooltip-caption">Desil 1</div><div class="map-tooltip-row"><span class="map-tooltip-label">Org</span><span class="map-tooltip-value">{desil1} ({pctDesil1}%)</span></div><div class="map-tooltip-row"><span class="map-tooltip-label">KK</span><span class="map-tooltip-value">{desil1KK} ({pctDesil1KK}%)</span></div>`,
        2: () =>
          `<div class="map-tooltip-caption">Desil 2</div><div class="map-tooltip-row"><span class="map-tooltip-label">Org</span><span class="map-tooltip-value">{desil2} ({pctDesil2}%)</span></div><div class="map-tooltip-row"><span class="map-tooltip-label">KK</span><span class="map-tooltip-value">{desil2KK} ({pctDesil2KK}%)</span></div>`,
        3: () =>
          `<div class="map-tooltip-caption">Desil 3</div><div class="map-tooltip-row"><span class="map-tooltip-label">Org</span><span class="map-tooltip-value">{desil3} ({pctDesil3}%)</span></div><div class="map-tooltip-row"><span class="map-tooltip-label">KK</span><span class="map-tooltip-value">{desil3KK} ({pctDesil3KK}%)</span></div>`,
        4: () =>
          `<div class="map-tooltip-caption">Desil 4</div><div class="map-tooltip-row"><span class="map-tooltip-label">Org</span><span class="map-tooltip-value">{desil4} ({pctDesil4}%)</span></div><div class="map-tooltip-row"><span class="map-tooltip-label">KK</span><span class="map-tooltip-value">{desil4KK} ({pctDesil4KK}%)</span></div>`,
        5: () =>
          `<div class="map-tooltip-caption">Desil 5</div><div class="map-tooltip-row"><span class="map-tooltip-label">Org</span><span class="map-tooltip-value">{desil5} ({pctDesil5}%)</span></div><div class="map-tooltip-row"><span class="map-tooltip-label">KK</span><span class="map-tooltip-value">{desil5KK} ({pctDesil5KK}%)</span></div>`,
        6: () =>
          `<div class="map-tooltip-caption">Desil 6–10</div><div class="map-tooltip-row"><span class="map-tooltip-label">Org</span><span class="map-tooltip-value">{desil610} ({pctDesil610}%)</span></div><div class="map-tooltip-row"><span class="map-tooltip-label">KK</span><span class="map-tooltip-value">{desil610KK} ({pctDesil610KK}%)</span></div>`,
        7: () =>
          `<div class="map-tooltip-caption">% Desil 1 / penduduk</div><div class="map-tooltip-row"><span class="map-tooltip-label">Org</span><span class="map-tooltip-value">{penerimaDesil}%</span></div><div class="map-tooltip-row"><span class="map-tooltip-label">KK</span><span class="map-tooltip-value">{penerimaDesilKK}%</span></div>`,
        8: () =>
          `<div class="map-tooltip-caption">% Desil 1+2 / penduduk</div><div class="map-tooltip-row"><span class="map-tooltip-label">Org</span><span class="map-tooltip-value">{penerimaDesil}%</span></div><div class="map-tooltip-row"><span class="map-tooltip-label">KK</span><span class="map-tooltip-value">{penerimaDesilKK}%</span></div>`,
      };
      const selectedRow = tooltipRows[selectedDesil] ? tooltipRows[selectedDesil]() : tooltipRows[1]();
      polygonTemplate.tooltipHTML = `
    <div class="map-tooltip-card">
      <div class="map-tooltip-title"><strong>{DESA}</strong> <span class="map-tooltip-sub">· {KECAMATAN}</span></div>
      <div class="map-tooltip-body">${selectedRow}</div>
    </div>
    `;
    }

    const allKecKeysChart = new Set();
    roteNdaoGeo.features.forEach((f) => {
      const kk = normalizeForMatch(f.properties?.KECAMATAN);
      if (kk) allKecKeysChart.add(kk);
    });
    const kecZoomFilter = resolveKecamatanIncludedFilter(
      mapLevel,
      mapUiSettings.kecamatanIncludedKeys,
      allKecKeysChart
    );

    polygonSeries.events.on('datavalidated', () => {
      polygonSeries.mapPolygons.each((polygon) => {
        const color = polygon.dataItem.dataContext.color;
        polygon.fill = color;
      });
      if (
        mapLevel === 'kecamatan' &&
        kecZoomFilter.active &&
        kecZoomFilter.set &&
        kecZoomFilter.set.size > 0
      ) {
        const bbox = {
          minLat: Infinity,
          maxLat: -Infinity,
          minLng: Infinity,
          maxLng: -Infinity,
        };
        let anyB = false;
        const feats = mapCombine.features || [];
        for (let fi = 0; fi < feats.length; fi++) {
          const f = feats[fi];
          const k = f.properties?._kecKeyNorm;
          if (!k || !kecZoomFilter.set.has(k)) continue;
          extendBboxFromGeometry(f.geometry, bbox);
          anyB = true;
        }
        if (anyB && Number.isFinite(bbox.minLat)) {
          map.zoomToRectangle(bbox.maxLat, bbox.maxLng, bbox.minLat, bbox.minLng, 0.88, true, 0);
        }
      } else {
        map.goHome(0);
      }
    });

    polygonTemplate.stroke = am4core.color(desilColors.stroke || '#ffffff');
    const hs = polygonTemplate.states.create('hover');
    hs.properties.fill = am4core.color(desilColors.hover || '#1565c0');

    if (previewMode && onManualCbRef.current) {
      polygonTemplate.cursorOverStyle = am4core.MouseCursorStyle.pointer;
      polygonTemplate.events.on('hit', (ev) => {
        const ctx = ev.target.dataItem?.dataContext;
        if (!ctx || !ctx._manualKey) return;
        onManualCbRef.current({
          manualKey: ctx._manualKey,
          displayName: ctx._displayLabel,
          currentColor: ctx.color,
        });
      });
    }

    if (mapUiSettings.showKecamatanLabels && mapCombine.features?.length) {
      const labelIncludedSet = kecZoomFilter.active ? kecZoomFilter.set : null;
      const pts = computeKecamatanLabelPoints(mapCombine.features, labelIncludedSet);
      const kecSeries = map.series.push(new am4maps.MapImageSeries());
      kecSeries.data = pts;
      const kecTpl = kecSeries.mapImages.template;
      kecTpl.nonScaling = true;
      kecTpl.propertyFields.latitude = 'latitude';
      kecTpl.propertyFields.longitude = 'longitude';
      const kecLbl = kecTpl.createChild(am4core.Label);
      kecLbl.text = '{title}';
      kecLbl.fontSize = 11;
      kecLbl.fill = am4core.color(desilColors.kecamatanLabel || '#1a1a1a');
      kecLbl.horizontalCenter = 'middle';
      kecLbl.verticalCenter = 'middle';
    }

    const citySeries = map.series.push(new am4maps.MapImageSeries());
    citySeries.data = cities;
    citySeries.dataFields.value = 'size';
    const city = citySeries.mapImages.template;
    city.nonScaling = true;
    city.propertyFields.latitude = 'latitude';
    city.propertyFields.longitude = 'longitude';
    const circle = city.createChild(am4core.Circle);
    circle.fill = am4core.color('#C7D0FF');
    circle.strokeWidth = 0;
    const circleHoverState = circle.states.create('hover');
    circleHoverState.properties.strokeWidth = 1;
    circle.tooltipText = '{tooltip}';
    circle.propertyFields.radius = 'size';

    return () => {
      if (am4ChartRef.current) {
        am4ChartRef.current.dispose();
        am4ChartRef.current = null;
      }
    };
  }, [mapCombine, selectedDesil, mapStyleFingerprint, mapDomId, dataSource, previewMode]);

  const desilLabel =
    dataSource === 'csv'
      ? 'Nilai (CSV)'
      : DESIL_OPTIONS.find((o) => o.value === selectedDesil)?.label || `Desil ${selectedDesil}`;

  const getIndividuValue = (row) => {
    if (!row) return 0;
    if (selectedDesil <= 6) {
      const key = selectedDesil === 6 ? 'Desil_6_10' : `Desil_${selectedDesil}`;
      return Number(row[key]) || 0;
    }
    return selectedDesil === 7
      ? Number(row.Desil_1) || 0
      : (Number(row.Desil_1) || 0) + (Number(row.Desil_2) || 0);
  };
  const getKKValue = (row) => {
    if (!row) return 0;
    if (selectedDesil <= 6) {
      const key = selectedDesil === 6 ? 'Desil_6_10_KK' : `Desil_${selectedDesil}_KK`;
      return Number(row[key]) || 0;
    }
    return selectedDesil === 7
      ? Number(row.Desil_1_KK) || 0
      : (Number(row.Desil_1_KK) || 0) + (Number(row.Desil_2_KK) || 0);
  };
  const getDisplayIndividu = (row) => {
    const val = getIndividuValue(row);
    if (selectedDesil >= 7) {
      const total = Number(row?.jumlahOrang) || 0;
      const pct = total > 0 ? ((val / total) * 100).toFixed(1) : '0';
      return `${numberWithCommas(val)} (${pct}%)`;
    }
    return numberWithCommas(val);
  };
  const getDisplayKK = (row) => {
    const val = getKKValue(row);
    if (selectedDesil >= 7) {
      const total = Number(row?.jumlahKK) || 0;
      const pct = total > 0 ? ((val / total) * 100).toFixed(1) : '0';
      return `${numberWithCommas(val)} (${pct}%)`;
    }
    return numberWithCommas(val);
  };
  const toSmallCaps = (t) =>
    (t || '').toString().toLowerCase().replace(/(?:^|\s)\S/g, (c) => c.toUpperCase());

  const handleDownloadPdf = async () => {
    if (!mapWrapRef.current || isLoadingMap || !listDesilDesa.length) return;
    setIsDownloadingPdf(true);
    try {
      const doc = new jsPDF('p', 'mm', 'a4');
      const pageW = doc.internal.pageSize.getWidth();
      const pageH = doc.internal.pageSize.getHeight();
      let y = 10;

      const addLogoAndSave = async () => {
        doc.setFontSize(7);
        doc.setTextColor(80, 80, 80);
        doc.text(TAGLINE, 25, y + 5, { maxWidth: pageW - 30 });
        y += 18;

        doc.setFontSize(11);
        doc.setTextColor(0, 0, 0);
        doc.text('Pilihan: ' + desilLabel, 14, y);
        y += 10;

        const mapCanvas = await html2canvas(mapWrapRef.current, {
          scale: 2,
          useCORS: true,
          logging: false,
          backgroundColor: '#ffffff',
        });
        const mapImgData = mapCanvas.toDataURL('image/png');
        const imgW = pageW - 28;
        const imgH = (mapCanvas.height * imgW) / mapCanvas.width;
        const maxH = pageH - y - 20;
        const finalH = Math.min(imgH, maxH);
        doc.addImage(mapImgData, 'PNG', 14, y, imgW, finalH);
        y += finalH + 12;

        doc.setFontSize(10);
        doc.setTextColor(30, 41, 59);

        if (dataSource === 'csv') {
          doc.text('Data CSV', 14, y);
          y += 8;
          const tableBody = listDesilDesa.map((row, idx) => [
            String(idx + 1),
            (row.desa || '').toString(),
            (row.kecamatan || '').toString(),
            String(row.nilai ?? ''),
          ]);
          doc.autoTable({
            startY: y,
            head: [['No', 'Desa', 'Kecamatan', 'Nilai']],
            body: tableBody,
            theme: 'grid',
            headStyles: { fillColor: [25, 118, 210], fontSize: 8 },
            bodyStyles: { fontSize: 7 },
            margin: { left: 14 },
            didDrawPage: () => {
              doc.setFontSize(8);
              doc.setTextColor(120, 120, 120);
              doc.text(`Unduh dari Tulufali - ${new Date().toLocaleDateString('id-ID')}`, 14, pageH - 8);
            },
          });
        } else {
          doc.text('Data per Desa — ' + desilLabel, 14, y);
          y += 8;
          const tableBody = listDesilDesa.map((row, idx) => [
            String(idx + 1),
            (row.desa || '').toString(),
            (row.kecamatan || '').toString(),
            numberWithCommas(Number(row.jumlahOrang) || 0),
            numberWithCommas(Number(row.jumlahKK) || 0),
            getDisplayIndividu(row),
            getDisplayKK(row),
          ]);
          doc.autoTable({
            startY: y,
            head: [['No', 'Desa', 'Kecamatan', 'Total individu', 'Total KK', 'Individu', 'KK']],
            body: tableBody,
            theme: 'grid',
            headStyles: { fillColor: [25, 118, 210], fontSize: 8 },
            bodyStyles: { fontSize: 7 },
            columnStyles: {
              0: { cellWidth: 10, halign: 'center' },
              1: { cellWidth: 38 },
              2: { cellWidth: 38 },
              3: { cellWidth: 26, halign: 'right' },
              4: { cellWidth: 22, halign: 'right' },
              5: { cellWidth: 30, halign: 'right' },
              6: { cellWidth: 26, halign: 'right' },
            },
            margin: { left: 14 },
            didDrawPage: () => {
              doc.setFontSize(8);
              doc.setTextColor(120, 120, 120);
              doc.text(`Unduh dari Tulufali - ${new Date().toLocaleDateString('id-ID')}`, 14, pageH - 8);
            },
          });
        }

        const dateStr = new Date().toLocaleDateString('id-ID');
        doc.save(`Peta-Desil-DTSEN-${desilLabel.replace(/\s+/g, '-')}-${dateStr.replace(/\//g, '-')}.pdf`);
        setIsDownloadingPdf(false);
      };

      const img = new Image();
      img.crossOrigin = 'Anonymous';
      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          canvas.width = img.naturalWidth;
          canvas.height = img.naturalHeight;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0);
          const logoData = canvas.toDataURL('image/png');
          doc.addImage(logoData, 'PNG', 14, y, 10, 10);
          doc.setFontSize(7);
          doc.setTextColor(80, 80, 80);
          doc.text(TAGLINE, 25, y + 6, { maxWidth: pageW - 30 });
        } catch (_) {
          doc.setFontSize(7);
          doc.text(TAGLINE, 14, y + 5);
        }
        y += 18;
        addLogoAndSave();
      };
      img.onerror = () => {
        doc.setFontSize(7);
        doc.text(TAGLINE, 14, y + 5);
        y += 18;
        addLogoAndSave();
      };
      img.src = TulufaliLogo;
    } catch (err) {
      console.error('handleDownloadPdf:', err);
      setIsDownloadingPdf(false);
    }
  };

  const downloadPdfHandlerRef = useRef(handleDownloadPdf);
  downloadPdfHandlerRef.current = handleDownloadPdf;
  useImperativeHandle(ref, () => ({
    downloadPdf: () => Promise.resolve(downloadPdfHandlerRef.current()),
  }), []);

  const legendPalette = legendMeta.tierPalette?.length
    ? legendMeta.tierPalette
    : ensureTierPalette([], legendMeta.tierCount || 4, []);

  return (
    <div className={customizationEmbed ? `${s.mapChart} ${s.mapChartCustomization}` : s.mapChart}>
      <div className={s.mapWrap} ref={mapWrapRef}>
        {!(customizationEmbed && previewMode) && (
          <div className={s.mapStats}>
            <div className={s.mapStatsLeft}>
              <h6 className={s.mapStatsTitle}>
                {previewMode ? 'Pratinjau warna peta' : 'SEBARAN PER DESIL (DTSEN)'}
              </h6>
              <p className={s.mapStatsYear}>{desilLabel}</p>
            </div>
            <div className={s.mapSelectWrap} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              {dataSource === 'api' ? (
                <Input
                  type="select"
                  value={selectedDesil}
                  onChange={(e) => setSelectedDesil(Number(e.target.value))}
                  className={s.mapSelect}
                >
                  {DESIL_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </Input>
              ) : (
                <span className="small text-muted">
                  Sumber: CSV{previewMode && customizationEmbed ? '' : ' (unggah di Pengaturan peta)'}
                </span>
              )}
              {!previewMode && (
                <Button
                  color="light"
                  size="sm"
                  onClick={handleDownloadPdf}
                  disabled={isDownloadingPdf || isLoadingMap || !listDesilDesa.length}
                  className={s.btnDownloadPdf}
                >
                  <i className={`fa fa-file-pdf-o ${s.pdfIcon}`} aria-hidden="true" />
                  {isDownloadingPdf ? ' ...' : ' PDF'}
                </Button>
              )}
            </div>
          </div>
        )}
        {isLoadingMap && dataSource === 'api' && (
          <div className={s.mapOverlay}>
            <div className={s.mapSpinner} />
            <span className={s.mapLoadingText}>Memuat data DTSEN...</span>
          </div>
        )}
        {!previewMode && !isLoadingMap && dataSource === 'api' && !listDesilDesa.length && (
          <div className={s.mapOverlay}>
            <span className={s.mapLoadingText}>Data DTSEN tidak tersedia</span>
          </div>
        )}
        <div className={s.map} id={mapDomId} />
        <div className={s.mapLegend}>
          <div className={s.legendItem}>
            <span className={s.legendColor} style={{ background: desilColors.none }} />
            <span className={s.legendLabel}>Tidak ada data</span>
          </div>
          {legendPalette.map((col, idx) => (
            <div key={idx} className={s.legendItem}>
              <span className={s.legendColor} style={{ background: col }} />
              <span className={s.legendLabel}>{legendMeta.labels[idx] || '—'}</span>
            </div>
          ))}
        </div>
      </div>

      {listDesilDesa.length > 0 && !previewMode && dataSource === 'csv' && (
        <div className={s.mapDataTableWrap}>
          <h6 className={s.mapDataTableTitle}>Data CSV</h6>
          <div className={s.mapDataTableScroll}>
            <Table responsive bordered size="sm" className={s.mapDataTable}>
              <thead>
                <tr>
                  <th className={s.thNo}>No</th>
                  <th>Desa</th>
                  <th>Kecamatan</th>
                  <th className={s.thNum}>Nilai</th>
                </tr>
              </thead>
              <tbody>
                {listDesilDesa.map((row, idx) => (
                  <tr key={`${row.desa}-${idx}`}>
                    <td className={s.thNo}>{idx + 1}</td>
                    <td>{toSmallCaps(row.desa)}</td>
                    <td>{toSmallCaps(row.kecamatan)}</td>
                    <td className={s.thNum}>{numberWithCommas(Number(row.nilai) || 0)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </div>
      )}

      {listDesilDesa.length > 0 && !previewMode && dataSource === 'api' && (
        <div className={s.mapDataTableWrap}>
          <h6 className={s.mapDataTableTitle}>Data per Desa — {desilLabel}</h6>
          <div className={s.mapDataTableScroll}>
            <Table responsive bordered size="sm" className={s.mapDataTable}>
              <thead>
                <tr>
                  <th className={s.thNo}>No</th>
                  <th>Desa</th>
                  <th>Kecamatan</th>
                  <th className={s.thNum}>Total individu</th>
                  <th className={s.thNum}>Total KK</th>
                  <th className={s.thNum}>Individu</th>
                  <th className={s.thNum}>KK</th>
                </tr>
              </thead>
              <tbody>
                {listDesilDesa.map((row, idx) => (
                  <tr key={row.id || `${row.desa}-${row.kecamatan}-${idx}`}>
                    <td className={s.thNo}>{idx + 1}</td>
                    <td>{toSmallCaps(row.desa)}</td>
                    <td>{toSmallCaps(row.kecamatan)}</td>
                    <td className={s.thNum}>
                      {numberWithCommas(Number(row.jumlahOrang) || 0)}
                    </td>
                    <td className={s.thNum}>
                      {numberWithCommas(Number(row.jumlahKK) || 0)}
                    </td>
                    <td className={s.thNum}>{getDisplayIndividu(row)}</td>
                    <td className={s.thNum}>{getDisplayKK(row)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </div>
      )}
    </div>
  );
});

Am4chartMapDesil.displayName = 'Am4chartMapDesil';

export default Am4chartMapDesil;
